import { createFileRoute, notFound } from "@tanstack/react-router";
import { BlogPostPage } from "@/components/calv/blog/BlogPostPage";
import { getPost, getAlternateSlug, type BlogPost } from "@/lib/blog";
import { SITE_URL } from "@/lib/locale";
import { pageHead } from "@/lib/seo";

type LoaderData = { post: BlogPost | null; alternateLocalePath: string };

export const Route = createFileRoute("/en/blog/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const post = await getPost(params.slug, "en");
    if (!post) return { post: null, alternateLocalePath: "/blog" };
    const alt = getAlternateSlug(post.dirKey, "de");
    return { post, alternateLocalePath: alt ? `/blog/${alt}` : "/blog" };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      const h = pageHead("blog", "en");
      return { meta: h.meta, links: h.links };
    }
    const deSlug = getAlternateSlug(post.dirKey, "de") ?? post.fm.slug;
    const canonical = `${SITE_URL}/en/blog/${post.fm.slug}`;
    const altDe = `${SITE_URL}/blog/${deSlug}`;
    return {
      meta: [
        { title: post.fm.seoTitle },
        { name: "description", content: post.fm.description },
        { property: "og:title", content: post.fm.seoTitle },
        { property: "og:description", content: post.fm.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "de_DE" },
        { property: "og:image", content: `${SITE_URL}${post.fm.ogImage ?? "/og/default-1200x630.png"}` },
        { property: "article:published_time", content: post.fm.datePublished },
        { property: "article:modified_time", content: post.fm.dateModified },
        { property: "article:author", content: "Calvarius GmbH" },
        { property: "article:section", content: post.fm.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.fm.seoTitle },
        { name: "twitter:description", content: post.fm.description },
        { name: "twitter:image", content: `${SITE_URL}${post.fm.ogImage ?? "/og/default-1200x630.png"}` },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "de", href: altDe },
        { rel: "alternate", hrefLang: "en", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: altDe },
      ],
    };
  },
  component: PostRoute,
  notFoundComponent: () => <div className="p-12">Post not found.</div>,
});

function PostRoute() {
  const { post } = Route.useLoaderData();
  if (!post) throw notFound();
  return <BlogPostPage post={post} />;
}
